import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin } from 'lucide-react'
import { weeklyScheduleApi } from '../services/api'
import PageMeta from '../components/seo/PageMeta'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { PageSlugs } from '../constants/pageSlugs'
import { useLanguage } from '../context/LanguageContext'

function parseDateLabel(label) {
  if (!label || typeof label !== 'string') return null
  const match = label.trim().match(/^(\d{1,2})\s*\/\s*(\d{1,2})$/)
  if (!match) return null
  const day = Number(match[1])
  const month = Number(match[2])
  if (day < 1 || day > 31 || month < 1 || month > 12) return null
  return { day, month }
}

function isSameCalendarDay(label, today = new Date()) {
  const parsed = parseDateLabel(label)
  if (!parsed) return false
  return parsed.day === today.getDate() && parsed.month === today.getMonth() + 1
}

function weekContainsToday(week, today = new Date()) {
  return (week.days || []).some((day) => isSameCalendarDay(day.date_label, today))
}

function WeekCard({ week, isAr, isCurrent, hereLabel, cardRef }) {
  const title = isAr ? week.title_ar : week.title_en

  return (
    <article
      ref={cardRef}
      className={`ws-week-card relative overflow-hidden rounded-xl border bg-white shadow-sm transition ${
        isCurrent ? 'border-accent/60 ring-1 ring-accent/30' : 'border-border'
      }`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {isCurrent ? (
        <div className="absolute end-2 top-2 z-10">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            {hereLabel}
          </span>
        </div>
      ) : null}
      <header
        className={`px-2 py-2 text-center text-xs font-semibold tracking-wide text-white sm:text-sm ${
          isCurrent ? 'bg-secondary' : 'bg-primary'
        }`}
      >
        {title}
      </header>
      <ul className="divide-y divide-border/70">
        {(week.days || []).map((day) => {
          const dayLabel = isAr ? day.day_ar : day.day_en
          const note = isAr ? day.note_ar : day.note_en
          const isToday = isSameCalendarDay(day.date_label)
          return (
            <li
              key={day.id || `${day.day_en}-${day.date_label}`}
              className={`grid grid-cols-[auto_1fr] gap-x-3 px-3 py-2 text-xs leading-snug sm:text-sm ${
                isToday ? 'bg-accent/12 font-semibold text-primary' : 'text-text'
              }`}
            >
              <span className={`whitespace-nowrap ${isToday ? 'text-accent-strong' : 'font-medium text-primary'}`}>
                {dayLabel}
                {day.date_label ? (
                  <span className={`ms-1.5 ${isToday ? 'text-accent-strong' : 'text-secondary'}`}>
                    {day.date_label}
                  </span>
                ) : null}
              </span>
              <span className={`min-w-0 text-start ${isToday ? 'text-primary' : 'text-text/80'}`}>
                {note || '\u00A0'}
              </span>
            </li>
          )
        })}
      </ul>
    </article>
  )
}

function CurrentWeekSpotlight({ week, isAr, t }) {
  if (!week) return null

  const title = isAr ? week.title_ar : week.title_en

  return (
    <section
      className="ws-current-week relative mb-10 overflow-hidden rounded-2xl border border-accent/25 bg-white shadow-lg"
      dir={isAr ? 'rtl' : 'ltr'}
      aria-label={t('weeklySchedule.currentWeekAria')}
    >
      <div className="absolute inset-0 gradient-brand-soft opacity-80" aria-hidden />
      <div className="absolute -end-16 -top-16 h-48 w-48 rounded-full bg-accent/20 blur-3xl" aria-hidden />
      <div className="absolute -start-10 bottom-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl" aria-hidden />

      <div className="relative p-5 sm:p-7 lg:p-8">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1.5 text-sm font-bold text-white shadow-sm">
            <MapPin className="h-4 w-4" strokeWidth={2.25} />
            {t('weeklySchedule.weAreHere')}
            <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary">
              {t('weeklySchedule.thisWeek')}
            </p>
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">{title}</h2>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {(week.days || []).map((day) => {
            const dayLabel = isAr ? day.day_ar : day.day_en
            const note = isAr ? day.note_ar : day.note_en
            const isToday = isSameCalendarDay(day.date_label)

            return (
              <div
                key={day.id || `${day.day_en}-${day.date_label}`}
                className={`rounded-xl border px-3 py-3 transition ${
                  isToday
                    ? 'border-accent bg-accent text-white shadow-md scale-[1.02]'
                    : 'border-border/80 bg-white/90 text-text'
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-sm font-bold ${isToday ? 'text-white' : 'text-primary'}`}>
                    {dayLabel}
                  </span>
                  <span className={`text-xs ${isToday ? 'text-white/90' : 'text-secondary'}`}>
                    {day.date_label}
                  </span>
                </div>
                {isToday ? (
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-white/90">
                    {t('weeklySchedule.today')}
                  </p>
                ) : null}
                <p className={`mt-2 text-sm leading-snug ${isToday ? 'text-white/95' : 'text-text/75'}`}>
                  {note || t('weeklySchedule.noEvent')}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default function WeeklySchedulePage() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const isAr = language === 'ar'
  const [schedule, setSchedule] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const currentCardRef = useRef(null)

  useEffect(() => {
    setLoading(true)
    weeklyScheduleApi
      .get(PageSlugs.weeklySchedule412)
      .then(({ data }) => setSchedule(data.data || data))
      .catch(() => setError(t('weeklySchedule.loadError')))
      .finally(() => setLoading(false))
  }, [t])

  const title = schedule
    ? isAr
      ? schedule.title_ar
      : schedule.title_en
    : t('weeklySchedule.fallbackTitle')
  const subtitle = schedule
    ? isAr
      ? schedule.subtitle_ar
      : schedule.subtitle_en
    : ''

  const weeks = schedule?.weeks || []
  const mainWeeks = weeks.filter((w) => w.week_number <= 16)
  const sideWeek = weeks.find((w) => w.week_number === 17) || weeks.find((w) => w.week_number > 16)

  const currentWeek = useMemo(() => {
    const today = new Date()
    return weeks.find((week) => weekContainsToday(week, today)) || null
  }, [weeks])

  const currentWeekNumber = currentWeek?.week_number ?? null
  const hereLabel = t('weeklySchedule.weAreHere')

  useEffect(() => {
    if (!currentWeekNumber || !currentCardRef.current) return
    const timer = window.setTimeout(() => {
      currentCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 350)
    return () => window.clearTimeout(timer)
  }, [currentWeekNumber, loading])

  return (
    <article className="header-offset">
      <PageMeta
        title={title}
        description={subtitle || t('weeklySchedule.metaDescription')}
        path={`/pages/${PageSlugs.weeklySchedule412}`}
      />

      <div className="relative overflow-hidden gradient-brand py-16 text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -end-20 top-0 h-64 w-64 rounded-full bg-accent blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-accent">
            {t('weeklySchedule.heroLabel')}
          </p>
          <h1 className="text-3xl font-bold sm:text-4xl lg:text-5xl">{title}</h1>
          {subtitle ? (
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">{subtitle}</p>
          ) : null}
        </div>
      </div>

      <div className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {loading ? (
            <LoadingSpinner className="py-24" />
          ) : error ? (
            <p className="py-16 text-center text-red-600">{error}</p>
          ) : (
            <div className="space-y-4">
              <CurrentWeekSpotlight week={currentWeek} isAr={isAr} t={t} />

              <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
                style={{ direction: isAr ? 'rtl' : 'ltr' }}
              >
                {mainWeeks.map((week) => {
                  const isCurrent = week.week_number === currentWeekNumber
                  return (
                    <WeekCard
                      key={week.id || week.week_number}
                      week={week}
                      isAr={isAr}
                      isCurrent={isCurrent}
                      hereLabel={hereLabel}
                      cardRef={isCurrent ? currentCardRef : undefined}
                    />
                  )
                })}
              </div>

              {sideWeek ? (
                <div className="mx-auto w-full max-w-md sm:max-w-lg">
                  <WeekCard
                    week={sideWeek}
                    isAr={isAr}
                    isCurrent={sideWeek.week_number === currentWeekNumber}
                    hereLabel={hereLabel}
                    cardRef={sideWeek.week_number === currentWeekNumber ? currentCardRef : undefined}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}
